import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import AppBar from '@material-ui/core/AppBar';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import useStyles from './styles';
import ApiService from '../../../services/ApiService';
import SaveViewPopUp from './SaveViewPopUp/SaveViewPopUp';
import DeleteViewPopUp from './DeleteViewPopUp/DeleteViewPopUp';
import {
  currentViewNameState,
  userPreferencesState,
  setCurrentViewName,
  addNewUserPreference,
  deleteUserPreference,
} from './TicketListSlice';
import { userInfoState } from '../HomeSlice';

function SaveButton(props) {
  const {
    disableElevation,
    fullWidth,
    variant,
    handleClickOnDeleteView,
    view,
    ...iconButtonProps
  } = props;
  return (
    <IconButton
      {...iconButtonProps}
      onClick={() => handleClickOnDeleteView(view.viewName)}
    />
  );
}

export default function AppBarTicketList(props) {
  const { children } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [displaySavePopup, setDisplaySavePopup] = React.useState(false);
  const [displayDeletePopUp, setDisplayDeletePopup] = React.useState(false);
  const [viewToDelete, setViewToDelete] = React.useState('');
  const currentViewName = useSelector(currentViewNameState);
  const userPreferences = useSelector(userPreferencesState);
  const user = useSelector(userInfoState);

  const { ticketListFiltered, filteredColumns, rowsFilters, sortConfig } =
    userPreferences.find(
      (userPreference) => userPreference.viewName === currentViewName,
    );
  const [tabs, setTabs] = React.useState(userPreferences);

  useEffect(() => {
    if (user.internal || user.hasEmilyDRights || user.externalMax) {
      const preferencesForInternalUser = userPreferences.filter(
        (pref) => pref.viewName !== 'tickets',
      );
      setTabs(preferencesForInternalUser);
    } else {
      const preferencesForExternalUser = userPreferences.filter(
        (pref) =>
          pref.viewName !== 'open tickets' &&
          pref.viewName !== 'closed tickets',
      );
      setTabs(preferencesForExternalUser);
    }
  }, [user, userPreferences]);

  function handleClickOnView(viewName) {
    dispatch(setCurrentViewName(viewName));
  }

  const handleClickOnSaveViews = () => {
    setDisplaySavePopup(true);
  };

  function closeSaveViewPopUp() {
    setDisplaySavePopup(false);
  }

  const handleClickOnDeleteView = (viewName) => {
    setViewToDelete(viewName);
    setDisplayDeletePopup(true);
  };

  function closeDeleteViewPopUp() {
    setViewToDelete('');
    setDisplayDeletePopup(false);
  }

  function saveView(viewName) {
    const viewFilter = {};

    rowsFilters.map((row) => {
      viewFilter[row.cell] = row.cell ? row.selectedOptions : [];
      return viewFilter;
    });

    const view = {};
    view.viewName = `${viewName} - ${ticketListFiltered}`;
    view.tableSetting = filteredColumns;
    view.filter = viewFilter;
    ApiService.saveUserPreference(view).then(() => {
      const userPreference = {};
      userPreference.viewName = viewName;
      userPreference.ticketListFiltered = ticketListFiltered;
      userPreference.filteredColumns = filteredColumns;
      userPreference.rowsFilters = rowsFilters;
      userPreference.sortConfig = sortConfig;
      dispatch(addNewUserPreference(userPreference));
      dispatch(setCurrentViewName(viewName));
      setDisplaySavePopup(false);
    });
  }

  function deleteView(deletedViewName) {
    const userPreferenceToDelete = userPreferences.find(
      (userPreference) => userPreference.viewName === deletedViewName,
    );
    const viewNameRegistered = `${deletedViewName} - ${userPreferenceToDelete.ticketListFiltered}`;
    ApiService.deleteUserPreference(viewNameRegistered).then(() => {
      if (deletedViewName === currentViewName && user.internal) {
        dispatch(setCurrentViewName('open tickets'));
      } else if (deletedViewName === currentViewName) {
        dispatch(setCurrentViewName('tickets'));
      }
      dispatch(deleteUserPreference(deletedViewName));
      setViewToDelete('');
      setDisplayDeletePopup(false);
    });
  }

  return (
    <AppBar elevation={0} position="static" className={classes.appBar}>
      <SaveViewPopUp
        openSaveViewPopUp={displaySavePopup}
        saveView={saveView}
        closeSaveViewPopUp={closeSaveViewPopUp}
      />
      <DeleteViewPopUp
        openDeleteViewPopUp={displayDeletePopUp}
        viewName={viewToDelete}
        deleteView={deleteView}
        closeDeleteViewPopUp={closeDeleteViewPopUp}
      />
      <Grid container spacing={0}>
        <Grid item xs={7}>
          <ButtonGroup className={classes.buttonGroup} variant="contained">
            {tabs.map((view, index) => {
              const key = `view-key-${index}`;
              return (
                <ButtonGroup
                  key={key}
                  variant="text"
                  className={classes.buttonTab}
                >
                  <Button
                    className={
                      currentViewName === view.viewName
                        ? classes.selectedTab
                        : classes.notSelectedTab
                    }
                    onClick={() => handleClickOnView(view.viewName)}
                  >
                    {t(view.viewName)}
                  </Button>
                  {view.viewName !== 'open tickets' &&
                    view.viewName !== 'closed tickets' &&
                    view.viewName !== 'tickets' && (
                      <SaveButton
                        view={view.viewName}
                        handleClickOnDeleteView={() =>
                          handleClickOnDeleteView(view.viewName)
                        }
                        className={
                          currentViewName === view.viewName
                            ? classes.closeButtonSelected
                            : classes.closeButton
                        }
                      >
                        <CloseIcon
                          fontSize="small"
                          className={
                            currentViewName === view.viewName
                              ? classes.closeIconSelected
                              : classes.closeIcon
                          }
                        />
                      </SaveButton>
                    )}
                </ButtonGroup>
              );
            })}
            <Button
              className={classes.saveView}
              onClick={handleClickOnSaveViews}
              startIcon={
                <Icon>
                  <img
                    alt="edit"
                    src={`${process.env.PUBLIC_URL}/Icon_Add_Favorite.svg`}
                  />
                </Icon>
              }
            />
          </ButtonGroup>
        </Grid>
        <Grid item xs={5} style={{ zIndex: 0 }}>
          {children}
        </Grid>
      </Grid>
    </AppBar>
  );
}
